import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Bold, Italic, Underline, List, ListOrdered, Eraser, Heading as HeadingIcon } from 'lucide-react';
import DOMPurify from 'dompurify';

// Restrict rich-text output to the tags this editor actually produces, and
// block all attributes to prevent event-handler / style injection XSS.
const SANITIZE_CONFIG = {
  // `div` is included because browser contentEditable emits <div> blocks on
  // Enter — without it, DOMPurify strips the blocks and every paragraph
  // collapses into one unbroken run of text in the rendered passage.
  ALLOWED_TAGS: ['p', 'div', 'br', 'strong', 'em', 'u', 'b', 'i', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'span'],
  ALLOWED_ATTR: [],
};
export const sanitizeHtml = (html) => DOMPurify.sanitize(html || '', SANITIZE_CONFIG);

/**
 * Minimal, dependency-free rich-text editor based on contentEditable.
 * Produces clean HTML (<p>, <strong>, <em>, <u>, <ul>/<ol>, <br>) in the `value` prop.
 * Backward-compatible: plain-text values render correctly too.
 */

function htmlLooksEmpty(html) {
  if (!html) return true;
  const stripped = html.replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trim();
  return stripped === '';
}

function isHtml(s) {
  return typeof s === 'string' && /<\/?(p|br|ul|ol|li|strong|b|em|i|u|div|span|h[1-6])\b/i.test(s);
}

function plainToHtml(s) {
  if (!s) return '';
  // Preserve line breaks so pasted multiline plain text still reads right.
  const esc = s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  return esc.split(/\n{2,}/).map(p => `<p>${p.replace(/\n/g, '<br>')}</p>`).join('');
}

export default function RichTextEditor({
  value,
  onChange,
  placeholder = 'Start typing…',
  rows = 4,
  className = '',
  compact = false,
  testId,
}) {
  const ref = useRef(null);
  const [focused, setFocused] = useState(false);
  // Track the last value we emitted, so we can skip the mirror-back effect
  // when the parent's re-render simply hands us back our own HTML. Without
  // this guard, every keystroke rewrites innerHTML and resets the cursor,
  // causing scrambled text (e.g. typing "Hello" → DOM "elloH") and apparent
  // loss of Shift+Enter line breaks.
  const lastEmittedRef = useRef('');

  // Mirror external value into the editor only when it *differs* from what's currently there.
  useEffect(() => {
    if (!ref.current) return;
    // If the parent is echoing back our own emit, do nothing — keeps cursor stable.
    if (value === lastEmittedRef.current) return;
    const raw = isHtml(value) ? (value || '') : plainToHtml(value || '');
    // Sanitize any HTML before putting it into the DOM. Blocks <script>, <img onerror=...>, etc.
    const incoming = sanitizeHtml(raw);
    if (ref.current.innerHTML !== incoming) {
      ref.current.innerHTML = incoming;
    }
    // eslint-disable-next-line
  }, [value]);

  const emit = useCallback(() => {
    if (!ref.current) return;
    const html = ref.current.innerHTML;
    const next = htmlLooksEmpty(html) ? '' : html;
    lastEmittedRef.current = next;
    onChange?.(next);
  }, [onChange]);

  const exec = useCallback((cmd, arg = null) => {
    ref.current?.focus();
    // eslint-disable-next-line
    document.execCommand(cmd, false, arg);
    emit();
  }, [emit]);

  const onKeyDown = (e) => {
    // Shift+Enter → explicit soft line break (<br>). Browsers vary in their
    // default behaviour; doing it ourselves ensures the line break appears
    // consistently and emits the new content immediately.
    if (e.key === 'Enter' && e.shiftKey) {
      e.preventDefault();
      // eslint-disable-next-line
      document.execCommand('insertLineBreak');
      emit();
      return;
    }
    // Keyboard shortcuts
    if ((e.ctrlKey || e.metaKey) && !e.shiftKey) {
      const k = e.key.toLowerCase();
      if (k === 'b') { e.preventDefault(); exec('bold'); return; }
      if (k === 'i') { e.preventDefault(); exec('italic'); return; }
      if (k === 'u') { e.preventDefault(); exec('underline'); return; }
    }
  };

  const onPaste = (e) => {
    // Paste as plain text to avoid importing unwanted styles
    e.preventDefault();
    const text = (e.clipboardData || window.clipboardData).getData('text/plain');
    // eslint-disable-next-line
    document.execCommand('insertText', false, text);
  };

  const btn = (label, Icon, cmd, arg, testPart) => (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => exec(cmd, arg)}
      title={label}
      className="p-1.5 rounded hover:bg-neutral-200 text-neutral-600 hover:text-neutral-900 transition"
      data-testid={testId ? `${testId}-${testPart}` : undefined}
    >
      <Icon size={14} />
    </button>
  );

  const minH = `${Math.max(rows * 22, 64)}px`;

  return (
    <div className={`border border-neutral-300 rounded-lg bg-white overflow-hidden focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-200 transition ${className}`}>
      <div className="flex items-center gap-0.5 px-1.5 py-1 border-b border-neutral-200 bg-neutral-50">
        {btn('Bold (Ctrl+B)', Bold, 'bold', null, 'bold')}
        {btn('Italic (Ctrl+I)', Italic, 'italic', null, 'italic')}
        {btn('Underline (Ctrl+U)', Underline, 'underline', null, 'underline')}
        <span className="mx-1 h-4 w-px bg-neutral-300" />
        {!compact && btn('Heading', HeadingIcon, 'formatBlock', 'H3', 'heading')}
        {btn('Bulleted list', List, 'insertUnorderedList', null, 'ul')}
        {btn('Numbered list', ListOrdered, 'insertOrderedList', null, 'ol')}
        <span className="mx-1 h-4 w-px bg-neutral-300" />
        {btn('Clear formatting', Eraser, 'removeFormat', null, 'clear')}
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onBlur={() => { setFocused(false); emit(); }}
        onFocus={() => setFocused(true)}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
        className="rte-content px-3 py-2 text-sm text-neutral-800 outline-none"
        style={{ minHeight: minH, lineHeight: 1.6 }}
        data-testid={testId}
        data-placeholder={placeholder}
      />
      <style>{`
        .rte-content[contenteditable=true]:empty:before {
          content: attr(data-placeholder);
          color: #94a3b8;
          pointer-events: none;
        }
        .rte-content p { margin: 0 0 8px 0; }
        .rte-content p:last-child { margin-bottom: 0; }
        .rte-content ul { list-style: disc; padding-left: 22px; margin: 4px 0 8px; }
        .rte-content ol { list-style: decimal; padding-left: 22px; margin: 4px 0 8px; }
        .rte-content li { margin: 2px 0; }
        .rte-content h3 { font-weight: 600; font-size: 15px; margin: 6px 0; }
        .rte-content strong, .rte-content b { font-weight: 600; }
      `}</style>
    </div>
  );
}

/**
 * Helper component to render rich text or plain text safely.
 * If `value` is HTML, renders via dangerouslySetInnerHTML with scoped styles.
 * Otherwise renders as plain text preserving whitespace.
 */
export function RichText({ value, className = '', style = {} }) {
  if (!value) return null;
  if (isHtml(value)) {
    return (
      <span
        className={`rte-rendered ${className}`}
        style={style}
        dangerouslySetInnerHTML={{ __html: sanitizeHtml(value) }}
      />
    );
  }
  return <span className={className} style={{ whiteSpace: 'pre-wrap', ...style }}>{value}</span>;
}

// Global styles for rendered rich text (scoped via class)
if (typeof document !== 'undefined' && !document.getElementById('rte-rendered-styles')) {
  const s = document.createElement('style');
  s.id = 'rte-rendered-styles';
  s.textContent = `
    .rte-rendered p { margin: 0 0 8px 0; }
    .rte-rendered p:last-child { margin-bottom: 0; }
    .rte-rendered div { margin: 0 0 8px 0; }
    .rte-rendered div:last-child { margin-bottom: 0; }
    .rte-rendered div:empty { min-height: 0.9em; }
    .rte-rendered ul { list-style: disc; padding-left: 22px; margin: 4px 0 8px; }
    .rte-rendered ol { list-style: decimal; padding-left: 22px; margin: 4px 0 8px; }
    .rte-rendered li { margin: 2px 0; }
    .rte-rendered h3 { font-weight: 600; font-size: 1.05em; margin: 6px 0; }
    .rte-rendered strong, .rte-rendered b { font-weight: 600; }
    .rte-rendered em, .rte-rendered i { font-style: italic; }
    .rte-rendered u { text-decoration: underline; }
  `;
  document.head.appendChild(s);
}
